import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  Tabs,
  Tab,
  Divider,
  Button,
  CircularProgress,
  Alert,
  Pagination,
  IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNotificacoes } from '../../hooks/useNotificacoes';
import ItemNotificacao from '../../components/Notificacoes/ItemNotificacao';
import { useNavigate } from 'react-router-dom';
import { NotificacaoInterface } from '../../types/NotificacaoInterface';
import { getSessionData } from '../../utils/storageUtils';

const PaginaNotificacoes: React.FC = () => {
  const [tabAtiva, setTabAtiva] = useState<number>(0);
  const [pagina, setPagina] = useState<number>(1);
  const itensPorPagina = 10;
  const navigate = useNavigate();

  // Verificar se o usuário está logado
  useEffect(() => {
    const usuarioId = getSessionData("DadosUsuarioID");
    const token = getSessionData("xc-helpdesk-token");
    
    if (!usuarioId || !token) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const { 
    notificacoes, 
    carregando, 
    erro, 
    carregarNotificacoes, 
    marcarComoLida,
    limparErro,
    totalPaginas,
    totalNotificacoes
  } = useNotificacoes({
    carregarAutomaticamente: true,
    apenasNaoLidas: tabAtiva === 1,
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabAtiva(newValue);
    setPagina(1);
    carregarNotificacoes(newValue === 1);
  };

  const handleMarcarComoLida = async (id: number) => {
    await marcarComoLida(id);
  };

  const handleNavegar = (notificacao: NotificacaoInterface) => {
    // Se não estiver lida, marca como lida
    if (!notificacao.lida) {
      marcarComoLida(notificacao.id);
    }

    // Navega para a página apropriada com base no tipo de notificação
    switch (notificacao.tipo) {
      case 'TICKET_CRIADO':
      case 'TICKET_ATUALIZADO':
      case 'TICKET_FECHADO':
      case 'TICKET_REABERTO':
      case 'TICKET_ATRIBUIDO':
      case 'COMENTARIO_ADICIONADO':
      case 'ANEXO_ADICIONADO':
        navigate(`/tickets/${notificacao.idRef}`);
        break;
      default:
        // Para outros tipos, não faz nada
        break;
    }
  };

  const handleChangePagina = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPagina(value);
  };

  const handleRecarregar = () => {
    limparErro();
    carregarNotificacoes(tabAtiva === 1);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Notificações
        </Typography>
        <IconButton 
          color="primary" 
          onClick={handleRecarregar}
          disabled={carregando}
          title="Recarregar notificações"
        >
          <RefreshIcon />
        </IconButton>
      </Box>
      
      <Paper elevation={2} sx={{ mt: 3, overflow: 'hidden' }}>
        <Tabs
          value={tabAtiva}
          onChange={handleTabChange}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="TODAS" />
          <Tab label="NÃO LIDAS" />
        </Tabs>
        
        <Divider />
        
        {erro && (
          <Alert 
            severity="error" 
            onClose={limparErro}
            sx={{ m: 2 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleRecarregar}
                disabled={carregando}
              >
                Tentar novamente
              </Button>
            }
          >
            {erro.message || 'Erro ao carregar notificações. Tente novamente.'}
          </Alert>
        )}
        
        <Box sx={{ minHeight: 400 }}>
          {carregando ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : notificacoes.length > 0 ? (
            <List sx={{ p: 0 }}>
              {notificacoes.map((notificacao) => (
                <ItemNotificacao
                  key={notificacao.id}
                  notificacao={notificacao}
                  onMarcarComoLida={handleMarcarComoLida}
                  onNavegar={handleNavegar}
                />
              ))}
            </List>
          ) : (
            <Box sx={{ p: 5, textAlign: 'center' }}>
              <Typography color="textSecondary" variant="h6">
                {tabAtiva === 0
                  ? 'Você não tem notificações.'
                  : 'Você não tem notificações não lidas.'}
              </Typography>
              <Button 
                variant="contained" 
                sx={{ mt: 2 }}
                onClick={() => navigate('/dashboard')}
              >
                Voltar para o Dashboard
              </Button>
            </Box>
          )}
        </Box>
        
        {totalPaginas > 1 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            p: 2,
            backgroundColor: '#f5f5f5',
            borderTop: '1px solid #e0e0e0'
          }}>
            <Pagination 
              count={totalPaginas} 
              page={pagina} 
              onChange={handleChangePagina} 
              color="primary" 
              size="medium"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default PaginaNotificacoes; 